<template>
  <fieldset
    class="MonthYearComponent"
    ref="componentRef"
  >
    <legend>{{ legend }}</legend>

    <div class="inputs">
      <v-text-field
        ref="$month"
        :id="`${id}-month`"
        :aria-label="`${legend} - ${t('common.dates.month')}`"
        :placeholder="t('common.dates.month')"
        :rules="[validationRules.required, validationRules.validMonth]"
        :model-value="month"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        maxlength="2"
        @change="onChange(setMonth, $event.target.value)"
      />

      <v-text-field
        :id="`${id}-year`"
        ref="$year"
        :aria-label="`${legend} - ${t('common.dates.year')}`"
        :placeholder="t('common.dates.year')"
        :maxlength="4"
        :rules="[validationRules.required, validationRules.validYear]"
        :model-value="year"
        :error="!!fieldValidationErrorMessage"
        :hide-details="true"
        @change="onChange(setYear, $event.target.value)"
      />
    </div>

    <div
      :id="`${id}-month-messages`"
      hidden
    ></div>
    <div
      :id="`${id}-year-messages`"
      hidden
    ></div>
    <div
      :id="`${id}-full-date-messages`"
      hidden
    ></div>

    <div aria-hidden="true">
      <v-text-field
        ref="$date"
        :id="`${id}-full-date`"
        class="full-date"
        v-model="date"
        :rules="[validationRules.required, validationRules.validDate]"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </fieldset>
</template>

<script setup lang="ts">
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { addNecessaryLeadingZeros } from '@/utils/textUtils'
  import { computed, onMounted, onUnmounted, ref, type Ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  // import { notFutureDate } from '@/utils/dateUtils'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { validMonth } from '@/utils/validations.utils'
  import ErrorMessageComponent from './ErrorMessageComponent.vue'

  const MONTH_FIELD_SIZE = 2
  const YEAR_FIELD_SIZE = 4

  const { t } = useI18n()
  const { notFutureDate } = useRules()

  interface PropsInterface {
    legend: string
    id: string
    month: string
    year: string
    validDateRule?: (d: Date, customText?: string) => boolean | string
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const emit = defineEmits(['update:month', 'update:year'])
  const validationRules = {
    required: (value: string) => !!value || t('common.validations.required'),
    validMonth: (value: string) => {
      return validMonth(value) || t('common.validations.month')
    },
    validYear: (value: string) => {
      const currentYear = new Date().getFullYear()
      const moreThan100YearsAgo = Number(value) > currentYear - 120

      return moreThan100YearsAgo || t('common.validations.year')
    },
    validDate: props.validDateRule ?? validateNotFutureDate
  }

  const fieldValidationErrorMessage = computed(() => {
    if (!$month?.value || !$year?.value) return ''

    return (
      $date.value?.errorMessages[0] ||
      $month.value?.errorMessages[0] ||
      $year.value?.errorMessages[0] ||
      ''
    )
  })

  const $date: Ref<any> = ref(null)
  const $month: Ref<any> = ref(null)
  const $year: Ref<any> = ref(null)

  const componentRef = ref<HTMLElement | null>(null)
  let observer: MutationObserver | null = null

  onMounted(() => {
    observer = startDuplicatedLabelControl(componentRef.value)
  })

  onUnmounted(() => {
    endDuplicatedLabelControl(observer)
  })

  const monthValue: Ref<string> = ref(props.month)
  const yearValue: Ref<string> = ref(props.year)

  const date = computed(() => {
    if (!isValidDate()) return null
    const monthNew = Number(monthValue.value)

    return new Date(`${yearValue.value}/${monthNew < 10 ? '0' + monthNew : monthNew}/01`)
  })

  function validateNotFutureDate(value: Date) {
    return notFutureDate(value, 'copy_future_date_validation')
  }

  function setMonth(value: string) {
    if (!runValidation([validationRules.validMonth], value)) {
      return
    }

    monthValue.value = addNecessaryLeadingZeros(value, MONTH_FIELD_SIZE)
  }

  function setYear(value: string) {
    if (!runValidation([validationRules.validYear], value)) {
      return
    }

    yearValue.value = addNecessaryLeadingZeros(value, YEAR_FIELD_SIZE)
  }

  function runValidation(rules: ValidationRuleType[], value: any): boolean {
    return rules.every((rule) => rule(value) === true)
  }

  function onChange(setterCallback: (value: string) => void, value: string) {
    setterCallback(value)

    if (monthValue.value && yearValue.value && yearValue.value.length === 4) {
      emit('update:month', monthValue.value)
      emit('update:year', yearValue.value)
    }
  }

  function isValidDate() {
    return (
      runValidation([validationRules.validMonth], monthValue.value) &&
      runValidation([validationRules.validYear], yearValue.value)
    )
  }
</script>

<style lang="scss" scoped>
  .MonthYearComponent {
    border: none;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-l;
    }

    .inputs {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: $gap-xl;

      :deep(.v-field) {
        background-color: $color-grey-200;
        border-radius: $b-radius-0;
        box-shadow: $box-shadow-0;
        transition: all 0.2s ease-in-out;

        .v-field__input {
          font-family: $font-family-primary;
          color: $color-neutral-dark;

          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }

        /*       &:focus-within {
        box-shadow: $box-shadow-focus;
      } */
      }

      :deep(.v-field--error) {
        background-color: $color-error-200;

        .v-field__input {
          &::placeholder {
            color: $color-neutral;
            opacity: 1;
          }
        }
      }
    }

    .full-date {
      // display: none;
      visibility: hidden;
      height: 0;
    }
  }
</style>
